import { useEffect, useState, type FormEvent } from 'react';
import Shell from '../components/Shell';
import { Field, TableSkeleton, ErrorState, useToast } from '../components/ui';
import { useCompany, useUpdateCompany, type CompanyFields } from '../lib/company';
import { useSession } from '../lib/session';
import { formOpened, formClosed } from '../platform';
import {
  Building2,
  Wallet,
  Shield,
  Check,
  RotateCcw,
  Bell,
  UserCheck,
  Phone,
  Mail,
  MapPin,
  Clock,
  type LucideIcon,
} from 'lucide-react';

type SettingsTab = 'company' | 'finance' | 'security';

interface TabConfig {
  key: SettingsTab;
  label: string;
  icon: LucideIcon;
  desc: string;
}

const TABS: TabConfig[] = [
  { key: 'company', label: 'Company Profile', icon: Building2, desc: 'Legal identity, GSTIN, and contact information' },
  { key: 'finance', label: 'Billing & Reminders', icon: Wallet, desc: 'Payment schedules and reminder intervals' },
  { key: 'security', label: 'Roles & Access Control', icon: Shield, desc: 'Role permissions and access boundaries' },
];

const EMPTY: CompanyFields = {
  name: '',
  legalName: '',
  gstNumber: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  reminderDays: 21,
};

const PRESETS = [7, 15, 21, 30, 45] as const;

export default function Settings() {
  const { data, isLoading, error, refetch } = useCompany();
  const actor = useSession((s) => s.actor);
  const update = useUpdateCompany();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<SettingsTab>('company');
  const [form, setForm] = useState<CompanyFields>(EMPTY);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!data || dirty) return;
    setForm({
      name: data.name,
      legalName: data.legalName ?? '',
      gstNumber: data.gstNumber ?? '',
      phone: data.phone ?? '',
      email: data.email ?? '',
      address: data.address ?? '',
      city: data.city ?? '',
      reminderDays: data.reminderDays,
    });
  }, [data, dirty]);

  const set = <K extends keyof CompanyFields>(k: K, v: CompanyFields[K]) => {
    setDirty(true);
    setForm((f) => ({ ...f, [k]: v }));
  };

  // While this form has unsaved edits, hold off a pending PWA update's
  // reload — same reasoning as the drawers (see platform/index.ts).
  useEffect(() => {
    if (!dirty) return;
    formOpened();
    return () => formClosed();
  }, [dirty]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync(form);
      setDirty(false);
      toast.show('ERP settings saved successfully');
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save settings', true);
    }
  }

  if (isLoading) {
    return (
      <Shell title="Company Settings" crumb="Administration">
        <div className="settings-card" style={{ padding: 24 }}>
          <TableSkeleton rows={5} cols={2} />
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell title="Company Settings" crumb="Administration">
        <div className="settings-card" style={{ padding: 24 }}>
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      </Shell>
    );
  }

  const initials = (form.name || 'VNS')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <Shell title="Company Settings & Profile" crumb="Administration">
      <div className="settings-wrap">
        {/* ======================= Hero Identity Card ======================= */}
        <div className="settings-hero-card">
          <div className="settings-hero-main">
            <div className="settings-hero-left">
              <div className="settings-avatar-brand">
                {initials || <Building2 size={24} />}
              </div>
              <div>
                <div className="settings-title-row">
                  <h2 className="settings-title">
                    {form.name || 'VNS Construction ERP'}
                  </h2>
                  <span className="settings-status-badge">
                    <span className="settings-status-dot" />
                    Active Enterprise Workspace
                  </span>
                </div>
                <div className="settings-meta-row">
                  {form.legalName && <span>{form.legalName}</span>}
                  {form.legalName && <span className="settings-meta-sep">•</span>}
                  <span>
                    GSTIN: <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)' }}>{form.gstNumber || 'Not Registered'}</strong>
                  </span>
                  <span className="settings-meta-sep">•</span>
                  <span>{form.city || 'Tamil Nadu, India'}</span>
                </div>
              </div>
            </div>

            <div className="settings-hero-right">
              <div className="settings-actor-chip">
                <span className="settings-actor-dot" />
                <span>Signed in as <strong style={{ color: 'var(--text)' }}>{actor?.fullName || actor?.email}</strong></span>
                <span className="settings-actor-badge">{actor?.role}</span>
              </div>
            </div>
          </div>

          {/* Tab Strip */}
          <div className="settings-tabs-strip">
            {TABS.map((t) => {
              const active = activeTab === t.key;
              const IconComp = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={`settings-tab-btn ${active ? 'active' : ''}`}
                >
                  <IconComp size={16} className="settings-tab-icon" />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ======================= Tab Forms Container ======================= */}
        <form onSubmit={onSubmit}>
          {/* TAB 1: Company Profile */}
          {activeTab === 'company' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div className="settings-card">
                <div className="settings-card-header">
                  <div className="settings-card-header-left">
                    <div className="settings-header-icon-wrap blue">
                      <Building2 size={18} />
                    </div>
                    <div>
                      <h3 className="settings-card-title">Legal Company Identity</h3>
                      <p className="settings-card-desc">
                        Printed on official invoices, client payment receipts, sub-contractor vouchers, and generated audit PDFs.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="settings-card-body">
                  <div className="settings-grid-3">
                    <Field label="Trading / Brand Name">
                      <input
                        value={form.name}
                        required
                        maxLength={120}
                        onChange={(e) => set('name', e.target.value)}
                      />
                    </Field>

                    <Field label="Legal Registered Entity Name" optional hint="Official name on incorporation documents">
                      <input
                        value={form.legalName}
                        maxLength={160}
                        onChange={(e) => set('legalName', e.target.value)}
                      />
                    </Field>

                    <Field label="GSTIN / Tax Identification" optional hint="15-digit Goods & Services Tax Number">
                      <input
                        value={form.gstNumber}
                        maxLength={15}
                        style={{ textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}
                        onChange={(e) => set('gstNumber', e.target.value)}
                      />
                    </Field>

                    <Field label="Primary Contact Phone" optional>
                      <div className="input-with-icon">
                        <Phone size={15} className="leading-icon" />
                        <input
                          type="tel"
                          value={form.phone}
                          maxLength={20}
                          onChange={(e) => set('phone', e.target.value)}
                        />
                      </div>
                    </Field>

                    <Field label="Official Accounts Email" optional>
                      <div className="input-with-icon">
                        <Mail size={15} className="leading-icon" />
                        <input
                          type="email"
                          value={form.email}
                          maxLength={120}
                          onChange={(e) => set('email', e.target.value)}
                        />
                      </div>
                    </Field>

                    <Field label="Operating City / Region" optional>
                      <div className="input-with-icon">
                        <MapPin size={15} className="leading-icon" />
                        <input
                          value={form.city}
                          maxLength={80}
                          onChange={(e) => set('city', e.target.value)}
                        />
                      </div>
                    </Field>

                    <div className="col-span-full">
                      <Field label="Registered Office Address" optional hint="Street address, building, industrial estate, and pin code">
                        <textarea
                          rows={2}
                          value={form.address}
                          maxLength={400}
                          onChange={(e) => set('address', e.target.value)}
                          style={{
                            width: '100%',
                            border: '1px solid var(--border-strong)',
                            borderRadius: 'var(--radius-sm)',
                            padding: '9.5px 12px',
                            fontSize: '13.5px',
                            fontFamily: 'var(--font-body)',
                            color: 'var(--text)',
                            background: '#ffffff',
                            outline: 'none',
                            resize: 'vertical',
                          }}
                        />
                      </Field>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: Billing & Reminders */}
          {activeTab === 'finance' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div className="settings-card">
                <div className="settings-card-header">
                  <div className="settings-card-header-left">
                    <div className="settings-header-icon-wrap amber">
                      <Bell size={18} />
                    </div>
                    <div>
                      <h3 className="settings-card-title">Automated Client Payment Reminders</h3>
                      <p className="settings-card-desc">
                        Configure recurring interval frequency for payment follow-ups when client projects have outstanding balances.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="settings-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div className="settings-grid-2">
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-soft)', marginBottom: 6 }}>
                        Reminder Repeat Frequency (Days)
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="number"
                          min={1}
                          max={365}
                          value={form.reminderDays}
                          onChange={(e) => set('reminderDays', Number(e.target.value))}
                          style={{ width: 110, fontWeight: 700 }}
                        />
                        <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>
                          days interval between repeat notifications
                        </span>
                      </div>
                      <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                        Allowed range: 1 to 365 calendar days.
                      </p>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-soft)', marginBottom: 6 }}>
                        Quick Frequency Presets
                      </label>
                      <div className="settings-preset-group">
                        {PRESETS.map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => set('reminderDays', d)}
                            className={`settings-preset-btn ${form.reminderDays === d ? 'active' : ''}`}
                          >
                            {d} Days {d === 21 ? '(Standard)' : ''}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Info Callout */}
                  <div className="settings-callout amber">
                    <Clock size={16} style={{ marginTop: 2, flexShrink: 0 }} />
                    <div>
                      <strong>Daily Notification Cycle:</strong> The reminder system evaluates all projects every morning at <strong>9:00 AM IST</strong>. Notifications appear on your top navigation bar and dashboard for any project whose last follow-up exceeds <strong>{form.reminderDays} days</strong>. Once the project balance is fully cleared (₹0), reminders stop automatically.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: Roles & Access Control */}
          {activeTab === 'security' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div className="settings-card">
                <div className="settings-card-header">
                  <div className="settings-card-header-left">
                    <div className="settings-header-icon-wrap indigo">
                      <UserCheck size={18} />
                    </div>
                    <div>
                      <h3 className="settings-card-title">Role-Based Access Controls (RBAC)</h3>
                      <p className="settings-card-desc">
                        Permissions architecture separating administrative control from site field logging.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="settings-card-body">
                  <div className="settings-grid-2">
                    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                          Admin Role
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Business Owner & Project Executives</span>
                      </div>
                      <ul style={{ fontSize: 12.5, color: 'var(--text-soft)', margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                        <li>Full approval authority for site submissions & invoices</li>
                        <li>Financial ledger, fund transfers, and bank balances</li>
                        <li>Client contract valuation & project setup</li>
                        <li>Export reports, company settings, and team access</li>
                      </ul>
                    </div>

                    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700, background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}>
                          Staff Role
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Site Engineers & Supervisors</span>
                      </div>
                      <ul style={{ fontSize: 12.5, color: 'var(--text-soft)', margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                        <li>Log daily material purchases and delivery invoices</li>
                        <li>Record subcontractor labour muster roll & trade counts</li>
                        <li>Log site material consumption & inter-project transfers</li>
                        <li>Submit site expense bills with review notes</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ======================= Sticky Action Footer ======================= */}
          <div className="settings-footer-bar" style={{ marginTop: 20 }}>
            <div className="settings-footer-actions">
              <button
                type="submit"
                disabled={!dirty || update.isPending}
                className="btn btn-primary btn-auto"
                style={{ borderRadius: 'var(--radius-sm)', padding: '8px 18px' }}
              >
                <Check size={16} />
                <span>{update.isPending ? 'Saving Settings…' : 'Save ERP Settings'}</span>
              </button>

              {dirty && (
                <button
                  type="button"
                  onClick={() => {
                    setDirty(false);
                    void refetch();
                  }}
                  className="btn btn-auto"
                  style={{ borderRadius: 'var(--radius-sm)', padding: '8px 16px' }}
                >
                  <RotateCcw size={15} />
                  <span>Discard Changes</span>
                </button>
              )}
            </div>

            <div className="settings-footer-status">
              {dirty ? (
                <span className="settings-unsaved-badge">
                  <span className="settings-status-dot" style={{ background: '#f59e0b' }} />
                  Unsaved changes
                </span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-faint)' }}>
                  <Check size={14} style={{ color: '#10b981' }} />
                  All configuration up to date
                </span>
              )}
            </div>
          </div>
        </form>
      </div>
    </Shell>
  );
}

